//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import Auth from '@aws-amplify/auth';
import { ForgotPassword } from 'aws-amplify-react';
import React from 'react';
import { Button } from 'unison';
import { Alert } from '@components/atoms/alert/Alert';

import styled from 'styled-components';
import logger from 'app/logger/logger';
import { CustomLabel } from './CustomLabel';
import { WideInput } from './WideInput';

class CuralateForgotPassword extends ForgotPassword {
  constructor(props) {
    super(props);
  }

  // Overrides aws-amplify-react implementation
  public showComponent() {
    const { authData = {} } = this.props;

    // Delivery seems to be result data about the "delivery" of the Password Reset Code
    // When this property is non-null, the Reset-Code is considered delivered.
    const { delivery } = this.state;

    return (
      <div>
        <h6>Reset your password</h6>
        {delivery || authData.username ? this.submitView() : this.sendView()}
        {delivery || authData.username ? (
          <ButtonFooter>
            <span className='cu-d-flex cu-align-items-center'>
              <Button
                className='cu-button-invisible cu-text-link-primary'
                onClick={this.send}
              >
                Resend Code
              </Button>
              &nbsp;|&nbsp;
              <Button
                className='cu-button-invisible cu-text-link-primary'
                onClick={() => this.changeState('signIn')}
              >
                Back
              </Button>
            </span>
            <Button className='cu-button-primary' onClick={this.submit}>
              Submit
            </Button>
          </ButtonFooter>
        ) : (
          <ButtonFooter>
            <Button
              className='cu-button-invisible cu-text-link-primary'
              onClick={() => this.changeState('signIn')}
            >
              Back to Sign In
            </Button>
            <Button className='cu-button-primary' onClick={this.send}>
              Send Code
            </Button>
          </ButtonFooter>
        )}
      </div>
    );
  }

  protected send() {
    const { authData = {} } = this.props;
    const username = this.inputs.username || authData.username || '';
    Auth.forgotPassword(username.toLowerCase())
      .then((data) => {
        logger.console.debug(data);
        this.setState({
          delivery: data.CodeDeliveryDetails,
        });
        this.props.onToast({
          title: 'Code Sent',
          message:
            'A password reset code has been sent to the email address you provided',
        });
      })
      .catch((err) => this.error(err));
  }

  protected submit() {
    const { alertType } = this.state;
    this.setState({ alertType: 'false' });
    const { authData = {} } = this.props;
    const { code, password } = this.inputs;
    const username = this.inputs.username || authData.username || '';

    Auth.forgotPasswordSubmit(username.toLowerCase(), code, password)
      .then((data) => {
        logger.console.debug(data);
        this.changeState('signIn');
        this.setState({ delivery: null });
        this.props.onToast({
          title: 'Password Reset',
          message: 'You have successfully reset your password.',
        });
      })
      .catch((err) => {
        let msg;
        if (err.code === 'CodeMismatchException') {
          msg = `Invalid verification code provided, please try again.`;
        } else if (err.code === 'LimitExceededException') {
          msg = `Attempt limit exceeded, please try after some time.`;
        } else {
          msg = `Password does not meet Bazaarvoice's password policy requirements.`;
        }
        this.setState({ alertType: err.code });
        this.error(msg);
      });
  }

  // Overrides aws-amplify-react implementation
  protected sendView() {
    return (
      <div>
        <CustomLabel label='Email Address'>
          <WideInput
            autoFocus={true}
            placeholder='Enter your email address'
            key='username'
            name='username'
            type='email'
            onChange={this.handleInputChange}
          />
        </CustomLabel>
      </div>
    );
  }

  // Overrides aws-amplify-react implementation
  protected submitView() {
    return (
      <div>
        <Alert
          type={
            this.state.alertType === 'InvalidPasswordException'
              ? 'error'
              : 'info'
          }
          className='cu-mx-0 cu-my-0'
        >
          <strong>Password Policy:</strong>
          <div>- Should be at least 8 characters long</div>
          <div>- Contain 1 uppercase letter</div>
          <div>- Contain 1 lowercase letter</div>
          <div>- Contain 1 number</div>
          <div>- Contain 1 special character</div>
        </Alert>
        <CustomLabel label='Password Reset Code'>
          <WideInput
            autoFocus={true}
            autoComplete='off'
            placeholder='Enter your password reset code'
            key='code'
            name='code'
            onChange={this.handleInputChange}
          />
        </CustomLabel>

        <CustomLabel label='New Password'>
          <WideInput
            autoFocus={true}
            autoComplete='new-password'
            placeholder='Enter your new password'
            key='password'
            name='password'
            type='password'
            onChange={this.handleInputChange}
          />
        </CustomLabel>
      </div>
    );
  }

  protected changeState(state: string, data?: any) {
    if (!this._validAuthStates.includes(state)) {
      // Reset state before leaving, since the base class won't.
      this.setState({ delivery: null, alertType: 'false' });
    }
    this.setState({ alertType: 'false' });
    return super.changeState(state, data);
  }
}

const ButtonFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default CuralateForgotPassword;
